/**
 * VariationChip Component
 * A chip component for displaying and selecting service variations
 * Based on LabelChip but optimized for variation selection behavior
 */

import { getVariationDisplayText } from '../services/serviceVariationsService';
import { Check } from 'lucide-react';

const VariationChip = ({ 
  variation, 
  isSelected = false,
  onSelect,
  size = 'medium',
  className = '',
  basePrice = null,
  baseDuration = 0,
  isMultipleChoice = false,
  serviceSubjectToEvaluation = false
}) => {
  // Early return if variation is null/undefined
  if (!variation) {
    return null;
  }

  // Size variants for mobile-friendly touch targets (iOS HIG compliant)
  const sizeClasses = {
    small: 'px-4 py-2.5 text-xs min-h-[44px]',
    medium: 'px-4 py-3 text-xs min-h-[48px]',
    large: 'px-5 py-3.5 text-sm min-h-[52px]'
  };

  // Enhanced color scheme for better mobile UX
  const getChipColors = () => {
    if (isSelected) {
      return {
        backgroundColor: '#ec4899', // pink-500
        textColor: '#ffffff',
        borderColor: '#ec4899',
        shadow: '0 4px 8px rgba(236, 72, 153, 0.3)'
      };
    }
    
    return {
      backgroundColor: '#f8fafc', // slate-50
      textColor: '#1e293b', // slate-800
      borderColor: '#cbd5e1', // slate-300
      shadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    };
  };

  const { backgroundColor, textColor, borderColor, shadow } = getChipColors();
  const displayText = getVariationDisplayText(variation, basePrice);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSelect) {
      if (!isMultipleChoice && isSelected) {
        // For single choice radio buttons, pass null to unselect
        onSelect(null);
      } else {
        onSelect(variation);
      }
    }
  };

  return (
    <label
      onClick={handleClick}
      className={`
        flex items-center w-full p-4 cursor-pointer
        transition-all duration-300 ease-in-out
        hover:bg-brand-dark/5 dark:hover:bg-brand-light/5
        touch-manipulation
        ${isSelected
          ? 'bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark dark:border-brand-light'
          : 'bg-transparent border border-brand-dark/10 dark:border-brand-light/10'
        }
        ${className}
      `}
    >
      {/* Square Checkmark - Luxury Design */}
      <div className="flex-shrink-0 mr-4">
        <div className={`
          w-5 h-5 border flex items-center justify-center transition-all duration-300
          ${isSelected
            ? 'border-brand-dark dark:border-brand-light bg-brand-dark dark:bg-brand-light'
            : 'border-brand-dark/30 dark:border-brand-light/30 bg-transparent'
          }
        `}>
          {isSelected && (
            <Check size={12} className="text-brand-light dark:text-brand-dark" strokeWidth={2.5} />
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          {/* Left Side: Name and Duration */}
          <div className="flex-1 min-w-0">
            {/* Variation Name */}
            <div className="font-medium text-brand-dark dark:text-brand-light leading-snug text-sm tracking-wide">
              {variation.name}
            </div>

            {/* Duration Info */}
            {baseDuration > 0 && (
              <div className="text-xs text-brand-dark/50 dark:text-brand-light/50 mt-1 uppercase tracking-wider">
                {(() => {
                  const totalMinutes = baseDuration + (variation.duration_delta || 0);
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;

                  if (totalMinutes <= 0) return "0min";

                  if (hours > 0 && minutes > 0) {
                    return `${hours}h ${minutes}min`;
                  } else if (hours > 0) {
                    return `${hours}h`;
                  } else {
                    return `${minutes}min`;
                  }
                })()}
              </div>
            )}
          </div>

          {/* Right Side: Price */}
          {basePrice !== null && (
            <div className="flex flex-col items-end flex-shrink-0">
              {/* Evaluation prefix for service */}
              {serviceSubjectToEvaluation && (
                <span className="text-[10px] text-brand-dark/50 dark:text-brand-light/50 mb-0.5 uppercase tracking-wider">a partir de</span>
              )}
              <div className="font-serif font-bold text-brand-dark dark:text-brand-light text-base whitespace-nowrap">
                R$ {(parseFloat(basePrice) + parseFloat(variation.price_delta || 0)).toFixed(2).replace('.', ',')}
              </div>
            </div>
          )}
        </div>

        {/* Price Evaluation Badge */}
        {variation.price_subject_to_evaluation && (
          <div className="flex items-center mt-2">
            <div className="flex items-center bg-transparent border border-brand-dark/20 dark:border-brand-light/20 px-2 py-1">
              <div className="w-1 h-1 bg-brand-dark dark:bg-brand-light mr-1.5"></div>
              <span className="text-[10px] font-medium text-brand-dark dark:text-brand-light uppercase tracking-wider">
                Valor definido na avaliação • Agende já
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Hidden Radio/Checkbox Input */}
      <input
        type={isMultipleChoice ? "checkbox" : "radio"}
        className="sr-only"
        checked={isSelected}
        onChange={handleClick}
        aria-label={`${isMultipleChoice ? 'Toggle' : 'Select'} variation: ${variation.name}`}
      />
    </label>
  );
};

/**
 * VariationGroup Component
 * Groups related variations together with a label
 */
export const VariationGroup = ({ 
  group, 
  selectedVariationId,
  selectedVariationIds = [],
  onVariationSelect,
  size = 'medium',
  className = '',
  showGroupName = true,
  basePrice = null,
  baseDuration = 0,
  serviceSubjectToEvaluation = false
}) => {
  if (!group || !Array.isArray(group.variations) || group.variations.length === 0) {
    return null;
  }

  const isMultipleChoice = group.multiple_choice || false;

  const getIsSelected = (variation) => {
    if (isMultipleChoice) {
      return selectedVariationIds.includes(variation.id);
    } else {
      return selectedVariationId === variation.id;
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Group Label - Luxury Design */}
      {showGroupName && (
        <div className="flex items-center gap-3 pb-2 border-b border-brand-dark/10 dark:border-brand-light/10">
          <div className="text-xs font-medium text-brand-dark dark:text-brand-light uppercase tracking-widest">
            {group.name}
          </div>
          {isMultipleChoice && (
            <div className="bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light text-[10px] px-2 py-0.5 uppercase tracking-wider">
              Múltipla escolha
            </div>
          )}
        </div>
      )}

      {/* Variations */}
      <div className="space-y-2">
        {group.variations.map((variation) => (
          <VariationChip
            key={variation.id}
            variation={variation}
            isSelected={getIsSelected(variation)}
            onSelect={onVariationSelect}
            size={size}
            basePrice={basePrice}
            baseDuration={baseDuration}
            isMultipleChoice={isMultipleChoice}
            serviceSubjectToEvaluation={serviceSubjectToEvaluation}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * ServiceVariations Component
 * Main component that displays all variation groups for a service
 */
export const ServiceVariations = ({ 
  variationGroups = [],
  selectedVariations = {},
  onVariationSelect,
  onVariationToggle,
  size = 'medium',
  className = '',
  showGroupNames = true,
  basePrice = null,
  baseDuration = 0,
  serviceSubjectToEvaluation = false
}) => {
  if (!Array.isArray(variationGroups) || variationGroups.length === 0) {
    return null;
  }

  const handleVariationSelect = (groupId, variation, group) => {
    if (group.multiple_choice) {
      // Use toggle for multiple choice groups
      if (onVariationToggle) {
        onVariationToggle(groupId, variation);
      }
    } else {
      // Use select for single choice groups
      if (onVariationSelect) {
        onVariationSelect(groupId, variation);
      }
    }
  };

  const getSelectedVariationIds = (groupId, group) => {
    const selection = selectedVariations[groupId];
    if (!selection) return [];

    if (group.multiple_choice) {
      // Handle both single object and array cases
      if (Array.isArray(selection)) {
        return selection.map(v => v.id);
      } else {
        // Single variation selected - return its ID in array
        return selection.id ? [selection.id] : [];
      }
    }

    return [];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {variationGroups.map((group) => (
        <VariationGroup
          key={group.id}
          group={group}
          selectedVariationId={!group.multiple_choice ? selectedVariations[group.id]?.id : undefined}
          selectedVariationIds={group.multiple_choice ? getSelectedVariationIds(group.id, group) : []}
          onVariationSelect={(variation) => handleVariationSelect(group.id, variation, group)}
          size={size}
          showGroupName={showGroupNames}
          basePrice={basePrice}
          baseDuration={baseDuration}
          serviceSubjectToEvaluation={serviceSubjectToEvaluation}
        />
      ))}
    </div>
  );
};

export default VariationChip;